import classNames from 'classnames';
import style from './style.module.scss';
import {IProductSection, IProductShort, IProperty, IPropertyValue} from 'modules/products/types';
import Link from 'components/CustomRoutingLink';
import React, {useMemo} from 'react';
import {plural} from 'core/helpers';
import {
    AGE_EXTRACT,
    GRAPE_CONTENT,
    MANUFACTURER,
    NET_VOLUME,
    SUGAR_TYPE,
} from 'modules/product-card/constants/characteristics';
import {IProduct} from 'modules/product-card/models/ProductResponse';
import {getProductSpecialCategory} from 'modules/product-card/helpers';
import {BRANDED_CATEGORIES} from 'modules/product-card/helpers/characteristics';
import {PRODUCT_PLACEHOLDER} from 'core/constants';
import {SECTION_CODE} from 'modules/product-card/constants';
import {Typography} from 'components/Typography';
import {IAnalyticsMeta} from 'core/types';
import {useProductPropertyClickAnalytics} from 'modules/products/hooks/gtag/useProductPropertyClickAnalytics';

interface IProps {
    properties: IProductShort['properties'];
    url: string;
    isListType?: boolean;
    topSection?: IProduct['topSection'];
    sections?: IProduct['sections'];
    canonSection?: IProductSection;
    id: IProductShort['id'];
    analytics?: IAnalyticsMeta;
}

// Конфигурация для отображения свойств
const PROPERTY_CONFIG = {
    // для обычного отображения (порядок: страна, тип виски/регион, цвет, сахар, объем)
    default: {
        color: 3,
        country: 0,
        netVolume: 5,
        region: 2,
        sugarType: 4,
        productType: 1,
    },
    // для списка (расширенный порядок)
    list: {
        color: 3,
        country: 0,
        grapeContent: 6,
        manufacturer: 7,
        ageExtract: 8,
        netVolume: 5,
        region: 1,
        productType: 2,
        styleWine: 9,
        sugarType: 4,
        barrell: 10,
        agingCategory: 11,
    },
};

// маппинг кодов свойств для фильтров
const FILTER_CODES = {
    color: 'color',
    country: 'country',
    grapeContent: 'grape',
    manufacturer: 'manufacturer',
    netVolume: 'volume',
    region: 'region',
    sugarType: 'sugar_type',
    productType: 'product_type',
    barrell: 'barrell',
    agingCategory: 'konyak_class',
    // styleWine: 'style_wine',
};

// форматирование значения свойства
const formatPropertyValue = (value: any, code: string, isListType?: boolean, isLast?: boolean) => {
    let content: string;
    let suffix = '';

    // обработка объекта IPropertyValue
    if ('object' === typeof value && value && 'value' in value) {
        content = value.value;
    } else {
        content = String(value);
    }

    // обработка для возраста
    if (code === AGE_EXTRACT.KEY) {
        content = `${content} ${plural(Number(content), ['год', 'года', 'лет'])}`;
    }

    // обработка для объема
    if (code === NET_VOLUME.KEY) {
        suffix = '\u00A0л';
    } else if (!isListType && !isLast) {
        suffix = ',\u00A0';
    }

    return (
        <Typography
            className={code === SUGAR_TYPE.KEY ? style.sugar : undefined}
            variants={{DT: 'label-14-r', TB: 'label-12-r', MB: 'label-12-r'}}
        >
            {content}
            {suffix}
        </Typography>
    );
};

// рендер свойства
const PropertyItem = ({
    id,
    analytics,
    value,
    filterUrl,
    code,
    isListType,
    isLast,
}: {
    value: any;
    filterUrl?: string;
    code: string;
    isListType?: boolean;
    isLast?: boolean;
} & Pick<IProps, 'id' | 'analytics'>) => {
    const productPropertyClickAnalytics = useProductPropertyClickAnalytics();
    const content = formatPropertyValue(value, code, isListType, isLast);

    if (filterUrl) {
        return (
            <Link
                className={classNames(style.subCategoryLink, {
                    [style.linkInList]: isListType,
                })}
                href={filterUrl}
                shallow={true}
                prefetch={false}
                onClick={productPropertyClickAnalytics(id, analytics, filterUrl)}
            >
                {content}
            </Link>
        );
    }

    return <>&nbsp;{content}&nbsp;</>;
};

// рендер свойства в списке
const ListPropertyItem = ({
    id,
    analytics,
    property,
    filterUrl,
    isListType,
}: {
    property: IProperty & {url?: string | string[]};
    filterUrl?: string | string[];
    isListType?: boolean;
} & Pick<IProps, 'id' | 'analytics'>) => {
    const {value, type, code, title} = property;

    const renderValue = () => {
        if ('array<object>' === type && Array.isArray(value) && 0 < value.length) {
            return value.map((item, idx) => {
                const grape = item.grape || '';
                const percentage = item.value ? ` ${item.value}%` : '';
                const displayValue = grape + percentage;

                return (
                    <PropertyItem
                        id={id}
                        analytics={analytics}
                        key={idx}
                        value={displayValue}
                        filterUrl={Array.isArray(filterUrl) ? filterUrl[idx] : undefined}
                        code={code}
                        isListType={isListType}
                    />
                );
            });
        }

        let displayValue: string;

        if ('object' === typeof value && value) {
            if ('translate' in value && value.translate) {
                displayValue = value.translate;
            } else if ('value' in value) {
                displayValue = value.value;
            } else {
                displayValue = String(value);
            }
        } else {
            displayValue = String(value);
        }

        return (
            <PropertyItem
                id={id}
                analytics={analytics}
                value={displayValue}
                filterUrl={'string' === typeof filterUrl ? filterUrl : undefined}
                code={code}
                isListType={isListType}
            />
        );
    };

    return (
        <li
            className={classNames(style.propertyListItem, {
                [style.propertyListItemListType]: isListType,
                [style.hidden]: !isListType,
            })}
        >
            <span className={style.propertyTitle}>{title}:</span>
            {renderValue()}
        </li>
    );
};

export const ProductProperties = ({
    id,
    analytics,
    properties,
    url,
    isListType,
    topSection,
    canonSection,
    sections = [],
}: IProps) => {
    // определяем, является ли продукт брендированным
    const isProductBranded = useMemo(() => {
        const productCategory = getProductSpecialCategory({
            ...PRODUCT_PLACEHOLDER,
            properties: properties ?? [],
            topSection: topSection ?? '',
            sections: sections ?? [],
        });
        return BRANDED_CATEGORIES.includes(productCategory);
    }, [properties, sections, topSection]);

    // обрабатываем свойства и создаем URL для фильтров
    const processedProperties = useMemo(() => {
        if (!properties) {
            return [];
        }

        return (
            properties
                .filter((property) => 'bool' !== property.type && 'int' !== property.type)
                // eslint-disable-next-line complexity
                .map((property) => {
                    const filterCode = FILTER_CODES[property.code as keyof typeof FILTER_CODES];
                    if (!filterCode) {
                        return null;
                    }

                    let filterUrl: string | string[] | undefined =
                        // добавляем для каталога /ksn (крепкого) код канонической секции товара
                        canonSection?.code && canonSection.code !== topSection && topSection === SECTION_CODE.KSN
                            ? `${url}/${canonSection?.code}`
                            : url;

                    if ('object' === property.type) {
                        const slug = (property.value as IPropertyValue)?.slug;
                        filterUrl += slug ? `/filter/${filterCode}-${slug}` : '';
                    } else if ('array<object>' === property.type && Array.isArray(property.value)) {
                        filterUrl = property.value.map((item) => `${filterUrl}/filter/${filterCode}-${item.slug}`);
                    } else {
                        const formatValue =
                            property.code === NET_VOLUME.KEY
                                ? String(property.value).replace('.', '_')
                                : property.value;
                        filterUrl += `/filter/${filterCode}-${formatValue}`;
                    }

                    // изменяем заголовок для производителя в брендированных продуктах
                    const title = property.code === MANUFACTURER.KEY && isProductBranded ? 'Бренд' : property.title;

                    return {
                        ...property,
                        title,
                        url: filterUrl,
                    };
                })
                .filter(Boolean)
        );
    }, [properties, canonSection?.code, topSection, url, isProductBranded]);

    // фильтруем и сортируем свойства
    const sortedProperties = useMemo(() => {
        const config = isListType ? PROPERTY_CONFIG.list : PROPERTY_CONFIG.default;
        const maxIndex = Math.max(...Object.values(config));
        const sorted = new Array(maxIndex + 1).fill(null);

        processedProperties.forEach((property) => {
            if (property) {
                const index = config[property.code as keyof typeof config];
                if (index !== undefined) {
                    sorted[index] = property;
                }
            }
        });

        return sorted.filter(Boolean).filter((property) => {
            // Фильтруем нулевые объемы
            if (property.code === NET_VOLUME.KEY) {
                return 0 !== property.value && '0' !== property.value;
            }

            // Фильтруем пустые массивы винограда
            if (property.code === GRAPE_CONTENT.KEY && Array.isArray(property.value)) {
                return 0 < property.value.length;
            }

            return true;
        });
    }, [isListType, processedProperties]);

    // формируем отсортированный список свойств для отображения в режиме списка
    const sortedListProperties = useMemo(() => {
        const config = PROPERTY_CONFIG.list;
        const maxIndex = Math.max(...Object.values(config));
        const sorted = new Array(maxIndex + 1).fill(null);

        processedProperties.forEach((property) => {
            if (property) {
                const index = config[property.code as keyof typeof config];
                if (index !== undefined) {
                    sorted[index] = property;
                }
            }
        });

        return sorted.filter(Boolean).filter((property) => {
            if (property.code === NET_VOLUME.KEY) {
                return 0 !== property.value && '0' !== property.value;
            }
            if (property.code === GRAPE_CONTENT.KEY && Array.isArray(property.value)) {
                return 0 < property.value.length;
            }
            return true;
        });
    }, [processedProperties]);

    const renderListProperties = useMemo(() => {
        // в режиме списка выводим все свойства списка,
        // в обычном режиме исключаем те, что уже выведены выше (конфиг default)
        const defaultCodes = new Set(Object.keys(PROPERTY_CONFIG.default));
        const source = isListType
            ? sortedListProperties
            : sortedListProperties.filter((property) => !defaultCodes.has(property.code));

        return source.map((property) => (
            <ListPropertyItem
                id={id}
                analytics={analytics}
                key={property.code}
                property={property}
                filterUrl={property.url}
                isListType={isListType}
            />
        ));
    }, [analytics, id, isListType, sortedListProperties]);

    if (isListType) {
        return <>{renderListProperties}</>;
    }

    return (
        <>
            {sortedProperties.map((property, index) => {
                return (
                    <PropertyItem
                        id={id}
                        analytics={analytics}
                        key={`${property.code}-${index}`}
                        value={property.value}
                        filterUrl={'string' === typeof property.url ? property.url : undefined}
                        code={property.code}
                        isListType={isListType}
                        isLast={index === sortedProperties.length - 1}
                    />
                );
            })}
            {renderListProperties}
        </>
    );
};
